import { useSyncExternalStore } from 'react';

/** What the user picked in Settings. System follows the device and changes with it. */
export type ThemeMode = 'light' | 'dark' | 'system';
/** The theme that is on screen. */
export type AppliedTheme = 'light' | 'dark';

export const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system'];

export function normalizeTheme(value?: unknown): ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

export function resolveTheme(mode: ThemeMode): AppliedTheme {
  if (mode !== 'system') return mode;
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
    ? 'dark'
    : 'light';
}

/** Sets the theme on :root and gives the status bar the matching colour. */
export function applyTheme(mode: ThemeMode = 'system'): AppliedTheme {
  const theme = resolveTheme(mode);
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.classList.remove('dark', 'dimmed');
  root.style.colorScheme = theme;

  const statusBar = themeColor('--bg-window', theme === 'dark' ? '#161b1e' : '#fefdf6');
  const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  if (metas.length === 0) {
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = statusBar;
    document.head.append(meta);
    return theme;
  }
  metas.forEach((meta) => {
    meta.content = statusBar;
  });
  return theme;
}

/** Calls back when the device switches between light and dark; returns how to stop. */
export function watchSystemTheme(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {};
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

function readAppliedTheme(): AppliedTheme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

function subscribeAppliedTheme(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  return () => observer.disconnect();
}

/** The theme on screen, for code that draws its own colours (diagrams, canvas). */
export function useAppliedTheme(): AppliedTheme {
  return useSyncExternalStore(subscribeAppliedTheme, readAppliedTheme, () => 'light');
}

/** A colour token as the current theme resolves it. */
export function themeColor(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}
